/* Game of Nuts theme: global behaviour. No dependencies. */
(() => {
  'use strict';

  const qs = (s, r = document) => r.querySelector(s);
  const qsa = (s, r = document) => Array.from(r.querySelectorAll(s));
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const inEditor = !!(window.Shopify && window.Shopify.designMode);

  /* ---------- Sticky header shadow ---------- */
  const header = qs('[data-header]');
  if (header) {
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---------- Mobile menu (drawer with focus trap) ---------- */
  const focusable = 'a[href], button:not([disabled]), summary, input, [tabindex]:not([tabindex="-1"])';
  const menu = qs('[data-mobile-menu]');
  const menuOpen = qs('[data-menu-open]');
  if (menu && menuOpen) {
    const open = () => {
      menu.hidden = false;
      document.body.classList.add('no-scroll');
      menuOpen.setAttribute('aria-expanded', 'true');
      const first = qs('[data-menu-close].mobile-menu__close', menu);
      if (first) first.focus();
    };
    const close = () => {
      menu.hidden = true;
      document.body.classList.remove('no-scroll');
      menuOpen.setAttribute('aria-expanded', 'false');
      menuOpen.focus();
    };
    menuOpen.addEventListener('click', open);
    qsa('[data-menu-close]', menu).forEach((el) => el.addEventListener('click', close));
    menu.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') return close();
      if (e.key !== 'Tab') return;
      const items = qsa(focusable, menu).filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
    // Close the drawer if the window grows to desktop width
    window.matchMedia('(min-width: 990px)').addEventListener('change', (m) => {
      if (m.matches && !menu.hidden) { menu.hidden = true; document.body.classList.remove('no-scroll'); }
    });
  }

  /* ---------- Search panel ---------- */
  const searchBtn = qs('[data-search-toggle]');
  const searchPanel = qs('[data-search-panel]');
  if (searchBtn && searchPanel) {
    searchBtn.addEventListener('click', () => {
      const show = searchPanel.hidden;
      searchPanel.hidden = !show;
      searchBtn.setAttribute('aria-expanded', String(show));
      if (show) { const input = qs('input[type="search"]', searchPanel); if (input) input.focus(); }
    });
    searchPanel.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { searchPanel.hidden = true; searchBtn.setAttribute('aria-expanded', 'false'); searchBtn.focus(); }
    });
  }

  /* ---------- Announcement rotation ---------- */
  qsa('[data-rotate]').forEach((el) => {
    const msgs = qsa('.announcement__msg', el);
    if (msgs.length < 2 || reduceMotion) return;
    const every = (parseInt(el.dataset.interval, 10) || 5) * 1000;
    let i = 0, timer;
    const tick = () => {
      msgs[i].classList.remove('is-active');
      i = (i + 1) % msgs.length;
      msgs[i].classList.add('is-active');
    };
    const start = () => { timer = setInterval(tick, every); };
    const stop = () => clearInterval(timer);
    start();
    el.addEventListener('mouseenter', stop);
    el.addEventListener('mouseleave', start);
  });

  /* ---------- Slider (hero, testimonials) ---------- */
  class Slider {
    constructor(root) {
      this.root = root;
      this.track = qs('[data-slider-track]', root);
      this.slides = qsa('[data-slide]', root);
      if (!this.track || this.slides.length < 2) return;
      this.prev = qs('[data-slider-prev]', root);
      this.next = qs('[data-slider-next]', root);
      this.dotsBox = qs('[data-slider-dots]', root);
      this.autoplay = (parseFloat(root.dataset.autoplay) || 0) * 1000;
      this.index = 0;
      this.paused = false;

      if (this.prev) this.prev.addEventListener('click', () => this.step(-1));
      if (this.next) this.next.addEventListener('click', () => this.step(1));
      this.buildDots();
      this.observe();
      this.setupAutoplay();
      root.__slider = this;
    }
    step(dir) {
      const target = this.index + dir;
      if (target < 0) return this.go(this.slides.length - 1);
      if (target >= this.slides.length) return this.go(0);
      this.go(target);
    }
    go(i, instant) {
      const slide = this.slides[i];
      if (!slide) return;
      this.track.scrollTo({ left: slide.offsetLeft, behavior: instant || reduceMotion ? 'auto' : 'smooth' });
    }
    buildDots() {
      if (!this.dotsBox) return;
      this.dots = this.slides.map((_, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'slider__dot';
        b.setAttribute('aria-label', `${i + 1} / ${this.slides.length}`);
        b.addEventListener('click', () => this.go(i));
        this.dotsBox.appendChild(b);
        return b;
      });
      this.mark(0);
    }
    mark(i) {
      this.index = i;
      if (this.dots) this.dots.forEach((d, n) => d.setAttribute('aria-current', String(n === i)));
    }
    observe() {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting && en.intersectionRatio >= 0.6) this.mark(this.slides.indexOf(en.target));
        });
      }, { root: this.track, threshold: [0.6] });
      this.slides.forEach((s) => io.observe(s));
    }
    setupAutoplay() {
      if (!this.autoplay || reduceMotion) return;
      const play = () => { clearInterval(this.timer); this.timer = setInterval(() => { if (!this.paused && !document.hidden) this.step(1); }, this.autoplay); };
      const pause = () => { this.paused = true; };
      const resume = () => { this.paused = false; };
      this.root.addEventListener('mouseenter', pause);
      this.root.addEventListener('mouseleave', resume);
      this.root.addEventListener('focusin', pause);
      this.root.addEventListener('focusout', resume);
      this.root.addEventListener('touchstart', () => { pause(); clearTimeout(this.resumeTimer); this.resumeTimer = setTimeout(resume, 8000); }, { passive: true });
      play();
    }
  }
  const initSliders = (scope = document) => qsa('[data-slider]', scope).forEach((el) => { if (!el.__slider) new Slider(el); });

  /* ---------- Reveal on scroll ---------- */
  let revealObserver;
  const initReveal = (scope = document) => {
    const items = qsa('.reveal', scope);
    if (!items.length) return;
    if (inEditor || reduceMotion || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    revealObserver = revealObserver || new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (en.isIntersecting) { en.target.classList.add('is-visible'); revealObserver.unobserve(en.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    items.forEach((el) => revealObserver.observe(el));
  };

  /* ---------- Order tracking form ---------- */
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-track-form]');
    if (!form) return;
    e.preventDefault();
    const num = (form.elements.number.value || '').trim();
    if (!num) return;
    window.open((form.dataset.base || 'https://t.17track.net/en#nums=') + encodeURIComponent(num), '_blank', 'noopener');
  });

  /* ---------- Cart count (also used after cart changes) ---------- */
  window.GON = window.GON || {};
  window.GON.updateCartCount = async () => {
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      qsa('[data-cart-count]').forEach((el) => { el.textContent = cart.item_count; el.hidden = cart.item_count === 0; });
    } catch (err) { /* leave the count as rendered */ }
  };

  const init = (scope) => { initSliders(scope); initReveal(scope); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  /* ---------- Theme editor hooks ---------- */
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:block:select', (e) => {
    const slider = e.target.closest('[data-slider]');
    if (slider && slider.__slider) slider.__slider.go(slider.__slider.slides.indexOf(e.target.closest('[data-slide]')), true);
  });
})();
